import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Moon, Sun, Star, Heart } from "@phosphor-icons/react";
import Navbar from "../components/Navbar";

const ZODIAC_GLYPHS = ["♈", "♉", "♊", "♋", "♌", "♍", "♎", "♏", "♐", "♑", "♒", "♓"];

export default function Landing() {
    return (
        <>
            <Navbar />
            <main className="min-h-screen pt-32 pb-20 px-6">
                {/* HERO */}
                <section className="max-w-6xl mx-auto text-center relative">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        className="flex justify-center mb-8"
                    >
                        <img
                            src="/logo-clean.png"
                            alt="SoulStar Dating"
                            className="w-72 sm:w-96 h-auto rounded-2xl shadow-2xl"
                            style={{ boxShadow: "0 0 60px rgba(230, 195, 135, 0.25)" }}
                            data-testid="hero-logo"
                        />
                    </motion.div>
                    <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="label-tracking text-gold mb-6"
                    >
                        SoulStar Dating · Where charts collide
                    </motion.p>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.1 }}
                        className="font-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight"
                        data-testid="hero-title"
                    >
                        Date who the<br />
                        <span className="italic text-gold">stars chose for you.</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="font-serif text-lg sm:text-xl text-muted mt-8 max-w-2xl mx-auto leading-relaxed"
                    >
                        A dating app where matches are scored by Sun, Moon, and Rising — not by photos and small talk.
                        Meet the souls who actually orbit yours.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.7 }}
                        className="mt-12 flex flex-wrap items-center justify-center gap-4"
                    >
                        <Link to="/register" className="btn-gold-solid" data-testid="hero-begin">
                            Find My Match
                        </Link>
                        <Link to="/knowledge" className="btn-gold" data-testid="hero-learn">
                            The Art Within
                        </Link>
                    </motion.div>

                    {/* Floating zodiac glyphs */}
                    <div className="mt-20 flex justify-center gap-6 flex-wrap opacity-50">
                        {ZODIAC_GLYPHS.map((g, i) => (
                            <motion.span
                                key={g}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: [0.3, 0.8, 0.3] }}
                                transition={{ duration: 4, delay: i * 0.15, repeat: Infinity }}
                                className="text-3xl text-gold font-serif"
                            >
                                {g}
                            </motion.span>
                        ))}
                    </div>
                </section>

                {/* FEATURES BENTO */}
                <section className="max-w-6xl mx-auto mt-32 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <FeatureCard
                        icon={<Heart weight="duotone" size={28} />}
                        title="Celestial Matching"
                        text="Every potential match is scored 0-100 across elements, modalities, and aspects. See your top picks first."
                    />
                    <FeatureCard
                        icon={<Sun weight="duotone" size={28} />}
                        title="Big Three Reading"
                        text="Your Sun, Moon, and Rising — interpreted by AI as a personal love letter from the cosmos."
                    />
                    <FeatureCard
                        icon={<Moon weight="duotone" size={28} />}
                        title="Living Horoscopes"
                        text="Daily, monthly, and yearly readings tuned to your full chart — channeled through Gemini AI."
                    />
                    <FeatureCard
                        icon={<Star weight="duotone" size={28} />}
                        title="Synastry Readings"
                        text="When you match, see the deep cosmic chemistry between your two charts. Where you flow. Where you spark."
                        gold
                    />
                    <FeatureCard
                        icon={<Sun weight="duotone" size={28} />}
                        title="Natal Birth Chart"
                        text="The full sky at your moment of arrival. Ten planets, twelve houses, written in glyphs of gold."
                    />
                    <FeatureCard
                        icon={<Moon weight="duotone" size={28} />}
                        title="Ancient Wisdom"
                        text="A living library of astrology — the elements, the houses, the transits, the sacred geometry of fate."
                    />
                </section>

                {/* FOOTER CTA */}
                <section className="max-w-4xl mx-auto mt-32 text-center">
                    <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">
                        Your soulmate has a chart.
                    </h2>
                    <p className="font-serif text-muted text-lg mt-6 max-w-xl mx-auto">
                        Stop swiping on shadows. Begin reading the language the universe has spoken since before language existed.
                    </p>
                    <Link to="/register" className="btn-gold-solid inline-block mt-10" data-testid="footer-cta">
                        Begin Your Journey
                    </Link>
                </section>

                {/* LEGAL FOOTER */}
                <footer className="max-w-6xl mx-auto mt-24 pt-8 border-t border-white/10 text-center text-xs text-muted">
                    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
                        <Link to="/terms" className="hover:text-gold transition-colors" data-testid="footer-terms">
                            Terms of Service
                        </Link>
                        <Link to="/privacy" className="hover:text-gold transition-colors" data-testid="footer-privacy">
                            Privacy Policy
                        </Link>
                        <Link to="/knowledge" className="hover:text-gold transition-colors">
                            Astrology Basics
                        </Link>
                        <a href="mailto:support@soulstar.love" className="hover:text-gold transition-colors">
                            Contact
                        </a>
                    </div>
                    <p className="mt-4 label-tracking">© 2026 SoulStar Dating · For entertainment purposes. 18+ only.</p>
                </footer>
            </main>
        </>
    );
}

function FeatureCard({ icon, title, text, gold }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`${gold ? "glass-gold" : "glass"} p-8 hover-lift`}
        >
            <div className="text-gold mb-4">{icon}</div>
            <h3 className="font-serif text-2xl mb-3">{title}</h3>
            <p className="text-muted leading-relaxed text-sm">{text}</p>
        </motion.div>
    );
}
